import React, { useCallback, useEffect, useRef } from 'react';
import { Button, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useConnection } from '@sezenta/connection';
import { getRefreshTokenCookie } from '../context/ContractHelpers';

const NOTIF_KEY = 'access-changed';

// CR004 — permissions are baked into the token when it's issued, so an admin
// changing this user's role doesn't affect their live session until a new token
// is minted. This watcher asks the server (cheaply, on mount and when the tab
// regains focus) whether the token is stale, and if so shows a one-tap "Refresh"
// prompt that re-mints a token for the current project and reloads.
export const SessionFreshnessWatcher: React.FC = () => {
  const connection = useConnection();
  const { contractId } = useParams<{ contractId: string }>();
  const cid = contractId ?? '1';
  const [cookies] = useCookies(['refreshToken']);
  const shownRef = useRef(false);
  const lastCheckRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const sw = await connection.post(`auth/switch-contract/${cid}`);
      connection.login(
        sw.data.accessToken,
        sw.data.refreshToken ?? getRefreshTokenCookie() ?? cookies.refreshToken,
      );
    } catch {
      // fall through — a full reload still re-reads the current cookies
    }
    notification.destroy(NOTIF_KEY);
    window.location.reload();
  }, [connection, cid, cookies.refreshToken]);

  const check = useCallback(async () => {
    // Throttle: at most once every 20s, and never twice while the prompt is up.
    const now = Date.now();
    if (shownRef.current || now - lastCheckRef.current < 20000) return;
    lastCheckRef.current = now;
    try {
      const r = await connection.get('auth/session-status');
      if (r.data?.stale) {
        shownRef.current = true;
        notification.warning({
          key: NOTIF_KEY,
          message: 'Your access changed',
          description: 'Please refresh to update your permissions.',
          duration: 0,
          btn: (
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={refresh}
              style={{ background: '#26A3A7' }}
            >
              Refresh
            </Button>
          ),
        });
      }
    } catch {
      // ignore transient errors — this is best-effort
    }
  }, [connection, refresh]);

  useEffect(() => {
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [check]);

  return null;
};

export default SessionFreshnessWatcher;
